/* ==========================================================================
   Ormonde Jayne — PDP (product-main-new) behaviour
   - header offset for the sticky gallery
   - gallery (hero + thumbs / mobile peek slider + dots, video, variant → slide)
   - Compact size picker → hidden <variant-radios> (global.js does the rest)
   - variant sync: sticky bar, drawer, sold-out state, services
   - sticky add-to-cart bar
   - drawer: Send a hint (app proxy) + Engraving & gift services (cart add)
   Depends on: global.js (VariantRadios, trapFocus, Shopify.formatMoney,
   getMemberSalePricing), jQuery + cart-popup (cart notification),
   product-form-new.js (<product-form> submit).
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-oj-pdp]');
  if (!root) return;

  var cfg = window.OJ_pdp || {};
  var strings = cfg.strings || {};
  var isRtl = (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
  var SMALL_VARIANTS = ['10ml', '2 x 10ml', '4x10ml'];

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait || 120);
    };
  }
  function isDesktop() { return window.matchMedia('(min-width: 1024px)').matches; }
  function money(cents) {
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, window.currency_symbol || window.money_format || '£{{amount}}');
      }
    } catch (e) { /* fall through */ }
    return (cents / 100).toFixed(2);
  }
  function variantPrice(v) {
    if (!v) return 0;
    if (typeof window.getMemberSalePricing === 'function') {
      var p = window.getMemberSalePricing(v);
      if (p && p.price) return p.price;
    }
    return v.price || 0;
  }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---------------------------------------------------------------------
     Header offset (fixed nav) → --oj-header-h
     --------------------------------------------------------------------- */
  function setHeaderOffset() {
    var nav = document.querySelector('nav.main-header-nav');
    var h = nav ? nav.getBoundingClientRect().height : 80;
    if (!h || h > 240) h = 80;
    document.documentElement.style.setProperty('--oj-header-h', Math.round(h) + 'px');
  }
  setHeaderOffset();
  window.addEventListener('resize', debounce(setHeaderOffset, 150));
  window.addEventListener('load', setHeaderOffset);

  /* ---------------------------------------------------------------------
     Variant data
     --------------------------------------------------------------------- */
  var variantRadios = root.querySelector('variant-radios');
  var variants = [];
  if (variantRadios) {
    try { variants = JSON.parse(variantRadios.querySelector('[type="application/json"]').textContent) || []; } catch (e) { variants = []; }
  }
  if (!variants.length && window.product && window.product.variants) variants = window.product.variants;

  function findVariant(id) {
    id = String(id);
    for (var i = 0; i < variants.length; i++) if (String(variants[i].id) === id) return variants[i];
    return null;
  }
  var currentVariant = findVariant(root.dataset.selectedVariant) || variants[0] || null;

  /* ---------------------------------------------------------------------
     Gallery
     --------------------------------------------------------------------- */
  var Gallery = (function () {
    var el = root.querySelector('[data-oj-gallery]');
    if (!el) return null;
    var track = el.querySelector('[data-oj-gallery-track]');
    var slides = $all('[data-oj-slide]', track);
    var thumbs = $all('[data-oj-thumb]', el);
    var dots = $all('[data-oj-dot]', el);
    var active = parseInt(el.getAttribute('data-initial-index'), 10) || 0;
    if (!slides.length) return null;

    function padStart() {
      var cs = getComputedStyle(track);
      return parseFloat(isRtl ? cs.paddingRight : cs.paddingLeft) || 0;
    }
    function offsetOf(slide) {
      var tr = track.getBoundingClientRect();
      var sr = slide.getBoundingClientRect();
      return isRtl ? (sr.right - tr.right + padStart()) : (sr.left - tr.left - padStart());
    }
    function setActive(i) {
      active = i;
      thumbs.forEach(function (t, k) {
        t.classList.toggle('is-active', k === i);
        t.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });
      dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
      slides.forEach(function (s, k) {
        var v = s.querySelector('video');
        if (!v) return;
        if (k === i) { v.play().catch(function () {}); } else { v.pause(); try { v.currentTime = 0; } catch (e) {} }
      });
    }
    function goTo(i, smooth) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      var delta = offsetOf(slides[i]);
      if (Math.abs(delta) > 1) {
        try { track.scrollBy({ left: delta, behavior: smooth ? 'smooth' : 'auto' }); }
        catch (e) { track.scrollLeft += delta; }
      }
      setActive(i);
    }
    function nearest() {
      var best = 0, bd = Infinity;
      slides.forEach(function (s, k) {
        var d = Math.abs(offsetOf(s));
        if (d < bd) { bd = d; best = k; }
      });
      return best;
    }
    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        var n = nearest();
        if (n !== active) setActive(n);
      });
    }, { passive: true });
    thumbs.forEach(function (t) { t.addEventListener('click', function () { goTo(parseInt(t.dataset.ojThumb, 10), true); }); });
    dots.forEach(function (d) { d.addEventListener('click', function () { goTo(parseInt(d.dataset.ojDot, 10), true); }); });

    function goToVariant(id) {
      id = String(id);
      var idx = -1;
      slides.forEach(function (s, k) {
        if (idx >= 0) return;
        var ids = (' ' + (s.dataset.variantIds || '') + ' ');
        if (ids.indexOf(' ' + id + ' ') >= 0) idx = k;
      });
      if (idx >= 0 && idx !== active) goTo(idx, !isDesktop());
    }

    // Initial position (after layout); keep aligned on resize.
    requestAnimationFrame(function () { goTo(active, false); });
    window.addEventListener('resize', debounce(function () { goTo(active, false); }, 150));

    return { goTo: goTo, goToVariant: goToVariant };
  })();
  window.OJGallery = Gallery;

  /* ---------------------------------------------------------------------
     Elements shared by the sync + drawer + sticky modules
     --------------------------------------------------------------------- */
  var sizeButtons = $all('[data-oj-size]', root);
  var atcBtn = root.querySelector('[data-oj-atc]');
  var atcLabel = root.querySelector('[data-oj-atc-label]');
  var giftLink = root.querySelector('[data-oj-drawer-open="gift"]');

  var sticky = document.querySelector('[data-oj-sticky]');
  var stickyInput = sticky ? sticky.querySelector('[data-oj-sticky-variant]') : null;
  var stickyBtn = sticky ? sticky.querySelector('[data-oj-sticky-atc]') : null;
  var stickyLabel = sticky ? sticky.querySelector('[data-oj-sticky-label]') : null;
  var stickySizes = sticky ? $all('[data-oj-sticky-size], [data-oj-sticky-size-inline]', sticky) : [];

  var drawerEl = document.querySelector('[data-oj-drawer]');
  var scrimEl = document.querySelector('[data-oj-scrim]');
  var deliveryPanel = root.querySelector('[data-oj-delivery]');
  var deliveryMarquee = root.querySelector('[data-oj-delivery-marquee]');
  var collectPanel = root.querySelector('[data-oj-collect]');
  var stickyMode = sticky ? sticky.querySelector('[data-oj-sticky-mode]') : null;
  var stickyThumb = sticky ? sticky.querySelector('[data-oj-sticky-thumb]') : null;

  function syncMarquee() {
    if (!deliveryMarquee || !deliveryPanel) return;
    var msgEl = deliveryPanel.querySelector('.comp-shipping__message');
    if (!msgEl) return;
    var text = (msgEl.textContent || '').trim();
    if (!text) return;
    if (!/[.!]$/.test(text)) text += '.';
    $all('[data-oj-marquee-text]', deliveryMarquee).forEach(function (el) { el.textContent = text; });
    deliveryMarquee.setAttribute('aria-label', text);
  }

  function sizeSuffix(v) {
    if (!v || !v.title || v.title === 'Default Title') return '';
    return ' · ' + v.title;
  }
  function isSmallVariant(v) { return !!(v && SMALL_VARIANTS.indexOf(v.title) >= 0); }
  function isCollect(v) {
    return !!(v && window.OJ_collectUK && window.OJ_collectOnly && window.OJ_collectOnly[String(v.id)]);
  }
  function variantImageSrc(v) {
    var fi = v && v.featured_image;
    if (!fi || !fi.src) return null;
    // Ask the CDN for a thumbnail-sized rendition rather than the full asset
    return fi.src + (fi.src.indexOf('?') >= 0 ? '&' : '?') + 'width=176';
  }

  /* ---------------------------------------------------------------------
     Sticky bar visibility
     --------------------------------------------------------------------- */
  var atcAbove = false;
  var drawerOpen = false;
  function updateSticky() {
    if (!sticky) return;
    var show = atcAbove && !drawerOpen;
    sticky.classList.toggle('is-visible', show);
    sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    if (show) sticky.removeAttribute('inert'); else sticky.setAttribute('inert', '');
    document.documentElement.classList.toggle('oj-sticky-active', show);
  }
  if (sticky && atcBtn && 'IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var top = en.rootBounds ? en.rootBounds.top : 0;
        atcAbove = !en.isIntersecting && en.boundingClientRect.bottom < top + 1;
      });
      updateSticky();
    }, { threshold: 0, rootMargin: '-72px 0px 0px 0px' });
    io.observe(atcBtn);
  } else if (sticky && atcBtn) {
    var onScroll = function () {
      atcAbove = atcBtn.getBoundingClientRect().bottom < 72;
      updateSticky();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------------------------------------------------------------
     Apply a variant to every dependent UI piece
     --------------------------------------------------------------------- */
  function applyVariant(v) {
    if (!v) return;
    currentVariant = v;
    var available = !!v.available;
    var priceText = money(variantPrice(v));

    sizeButtons.forEach(function (b) {
      b.setAttribute('aria-pressed', String(b.dataset.variantId) === String(v.id) ? 'true' : 'false');
    });

    if (atcBtn && !variantRadios) {
      if (available) atcBtn.removeAttribute('disabled'); else atcBtn.setAttribute('disabled', 'disabled');
    }
    var collect = isCollect(v);
    // Isolate the price as LTR so RTL locales render "£235.00", not "235.00£"
    var priceLtr = '⁦' + priceText + '⁩';
    if (atcLabel) {
      atcLabel.textContent = available
        ? (collect ? (strings.collectInStore || 'Collect in store') : (strings.addToCart || 'Add to cart')) + ' - ' + priceLtr
        : (strings.soldOut || 'Sold out');
    }

    if (stickyInput) stickyInput.value = v.id;
    if (stickyBtn) {
      if (available) stickyBtn.removeAttribute('disabled'); else stickyBtn.setAttribute('disabled', 'disabled');
    }
    if (stickyLabel) {
      stickyLabel.textContent = available
        ? (collect ? ((stickyBtn && stickyBtn.dataset.labelCollect) || strings.collectInStore || 'Collect in store') : ((stickyBtn && stickyBtn.dataset.labelAdd) || strings.add || 'Add')) + ' - ' + priceLtr
        : ((stickyBtn && stickyBtn.dataset.labelSoldout) || strings.soldOut || 'Sold out');
    }
    stickySizes.forEach(function (s) { s.textContent = sizeSuffix(v); });
    if (stickyMode && sticky) stickyMode.textContent = collect ? (sticky.dataset.collect || strings.collectInStore || '') : (sticky.dataset.conc || '');
    if (stickyThumb) {
      var src = variantImageSrc(v);
      if (src && stickyThumb.getAttribute('src') !== src) stickyThumb.setAttribute('src', src);
    }

    // Delivery vs collect-in-store panels
    if (deliveryPanel) deliveryPanel.hidden = collect;
    if (deliveryMarquee) deliveryMarquee.hidden = collect;
    if (collectPanel) collectPanel.hidden = !collect;
    // global.js rewrites the comp-shipping message on the same event; mirror it into the marquee afterwards
    setTimeout(syncMarquee, 0);

    if (Gallery) Gallery.goToVariant(v.id);

    Gift.setVariant(v, priceText);
    Hint.setVariant(v, priceText);

    if (giftLink) {
      var engravingVisible = Gift.engravingVisible();
      giftLink.textContent = engravingVisible ? (giftLink.dataset.labelFull || giftLink.textContent) : (giftLink.dataset.labelGift || giftLink.textContent);
    }
  }

  // Size picker → hidden radios → global.js VariantRadios
  sizeButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var v = findVariant(btn.dataset.variantId);
      if (!v) return;
      if (variantRadios && v.options) {
        var fieldsets = $all('fieldset', variantRadios);
        var changed = false;
        v.options.forEach(function (val, i) {
          var fs = fieldsets[i];
          if (!fs) return;
          var radio = $all('input[type="radio"]', fs).filter(function (r) { return r.value === val; })[0];
          if (radio && !radio.checked) { radio.checked = true; changed = true; }
        });
        if (changed) {
          variantRadios.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          applyVariant(v);
        }
      } else {
        applyVariant(v);
      }
    });
  });

  // Any variant change (size picker, browser back/forward, other scripts)
  if (variantRadios) {
    variantRadios.addEventListener('change', function () {
      // VariantRadios (global.js) registered first, so currentVariant is fresh.
      var v = variantRadios.currentVariant || currentVariant;
      if (v) applyVariant(findVariant(v.id) || v);
    });
  }

  /* ---------------------------------------------------------------------
     Drawer
     --------------------------------------------------------------------- */
  var Drawer = (function () {
    if (!drawerEl || !scrimEl) {
      return { open: function () {}, close: function () {}, showPanel: function () {}, el: null };
    }
    var kind = null;
    var lastTrigger = null;
    var panels = $all('[data-oj-panel]', drawerEl);
    var foots = $all('[data-oj-foot]', drawerEl);
    var title = drawerEl.querySelector('[data-oj-drawer-title]');
    var scrollBody = drawerEl.querySelector('.oj-drawer__body');

    function showPanel(name) {
      panels.forEach(function (p) { p.hidden = p.dataset.ojPanel !== name; });
      foots.forEach(function (f) { f.hidden = f.dataset.ojFoot !== name; });
      if (scrollBody) scrollBody.scrollTop = 0;
    }
    function focusFirst() {
      var panel = drawerEl.querySelector('[data-oj-panel]:not([hidden])');
      var first = panel ? panel.querySelector('input:not([type="hidden"]):not([type="checkbox"]), textarea, button, [type="checkbox"]') : null;
      var target = first || drawerEl.querySelector('[data-oj-drawer-close]') || drawerEl;
      if (typeof window.trapFocus === 'function') window.trapFocus(drawerEl, target);
      else target.focus();
    }
    function markToday() {
      var rows = $all('[data-oj-day]', drawerEl);
      if (!rows.length) return;
      var today = (new Date().getDay() + 6) % 7; // Monday = 0
      rows.forEach(function (r) { r.classList.toggle('is-today', parseInt(r.dataset.ojDay, 10) === today); });
    }
    function open(k, trigger) {
      kind = k;
      lastTrigger = trigger || document.activeElement;
      if (k === 'hint') {
        Hint.reset();
        showPanel('hint');
        if (title) title.textContent = drawerEl.dataset.titleHint || '';
      } else if (k === 'store') {
        markToday();
        showPanel('store');
        if (title) title.textContent = drawerEl.dataset.titleStore || '';
      } else {
        Gift.prepare();
        showPanel('gift');
        if (title) title.textContent = Gift.engravingVisible() ? (drawerEl.dataset.titleGift || '') : (drawerEl.dataset.titleGiftOnly || '');
      }
      drawerEl.setAttribute('aria-hidden', 'false');
      scrimEl.classList.add('is-open');
      drawerEl.classList.add('is-open');
      document.documentElement.classList.add('oj-drawer-open');
      drawerOpen = true;
      updateSticky();
      setTimeout(focusFirst, 320);
    }
    function close() {
      if (!drawerOpen) return;
      drawerOpen = false;
      drawerEl.classList.remove('is-open');
      scrimEl.classList.remove('is-open');
      drawerEl.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('oj-drawer-open');
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(lastTrigger && lastTrigger.focus ? lastTrigger : null);
      else if (lastTrigger && lastTrigger.focus) lastTrigger.focus();
      updateSticky();
      kind = null;
    }

    scrimEl.addEventListener('click', close);
    $all('[data-oj-drawer-close]', drawerEl).forEach(function (b) { b.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawerOpen) { e.preventDefault(); close(); }
    });
    $all('[data-oj-drawer-open]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); open(b.dataset.ojDrawerOpen, b); });
    });

    return { open: open, close: close, showPanel: showPanel, el: drawerEl, kind: function () { return kind; } };
  })();

  /* ---------------------------------------------------------------------
     Send a hint
     --------------------------------------------------------------------- */
  var Hint = (function () {
    var form = drawerEl ? drawerEl.querySelector('[data-oj-panel="hint"]') : null;
    if (!form) return { reset: function () {}, setVariant: function () {} };
    var fields = {
      senderName: form.querySelector('[name="senderName"]'),
      senderEmail: form.querySelector('[name="senderEmail"]'),
      recipientName: form.querySelector('[name="recipientName"]'),
      recipientEmail: form.querySelector('[name="recipientEmail"]')
    };
    var errorEl = form.querySelector('[data-oj-hint-error]');
    var submitBtn = drawerEl.querySelector('[data-oj-hint-submit]');
    var sentMsg = drawerEl.querySelector('[data-oj-hint-sent-msg]');
    var sizeEl = form.querySelector('[data-oj-drawer-size]');
    var priceEl = form.querySelector('[data-oj-drawer-price]');
    var busy = false;

    function isEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }
    function setInvalid(input, invalid) {
      var field = input.closest('.oj-field');
      if (field) field.classList.toggle('is-invalid', !!invalid);
      input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    }
    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || '';
      errorEl.classList.toggle('is-visible', !!msg);
    }
    function validate() {
      var ok = true;
      var vals = {};
      Object.keys(fields).forEach(function (k) {
        var input = fields[k];
        var v = (input.value || '').trim();
        vals[k] = v;
        var bad = !v || (input.type === 'email' && !isEmail(v));
        setInvalid(input, bad);
        if (bad) ok = false;
      });
      if (!ok) {
        var firstBad = Object.keys(fields).map(function (k) { return fields[k]; }).filter(function (i) { return i.getAttribute('aria-invalid') === 'true'; })[0];
        if (firstBad) firstBad.focus();
      }
      return ok ? vals : null;
    }
    function setBusy(b) {
      busy = b;
      if (!submitBtn) return;
      submitBtn.disabled = b;
      submitBtn.setAttribute('aria-busy', b ? 'true' : 'false');
      submitBtn.textContent = b ? (submitBtn.dataset.labelSending || submitBtn.dataset.label) : submitBtn.dataset.label;
    }
    function submit() {
      if (busy) return;
      showError('');
      var vals = validate();
      if (!vals) return;
      setBusy(true);
      var payload = {
        senderName: vals.senderName,
        senderEmail: vals.senderEmail,
        recipientName: vals.recipientName,
        recipientEmail: vals.recipientEmail,
        productGid: drawerEl.dataset.productGid,
        templateId: 'romantic',
        personalNote: '',
        sourcePlatform: 'pdp_block'
      };
      var url = '/apps/fs/hint/send?shop=' + encodeURIComponent(drawerEl.dataset.shopDomain || '');
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      }).then(function (res) {
        var ct = res.headers.get('content-type') || '';
        if (ct.indexOf('application/json') < 0) throw new Error(strings.errGeneric || 'Something went wrong. Please try again.');
        return res.json();
      }).then(function (data) {
        if (!data || !data.success) throw new Error((data && data.error) || strings.errGeneric || 'Something went wrong. Please try again.');
        if (sentMsg) sentMsg.textContent = (sentMsg.dataset.template || 'Your hint is on its way to {name}.').replace('{name}', vals.recipientName);
        Drawer.showPanel('hint-sent');
        var done = drawerEl.querySelector('[data-oj-foot="hint-sent"] button');
        if (done) setTimeout(function () { done.focus(); }, 50);
      }).catch(function (err) {
        showError((err && err.message) || strings.errGeneric || 'Something went wrong. Please try again.');
      }).then(function () { setBusy(false); });
    }
    if (submitBtn) submitBtn.addEventListener('click', submit);
    form.addEventListener('submit', function (e) { e.preventDefault(); submit(); });
    Object.keys(fields).forEach(function (k) {
      fields[k].addEventListener('input', function () { setInvalid(fields[k], false); showError(''); });
    });

    return {
      reset: function () {
        Object.keys(fields).forEach(function (k) { setInvalid(fields[k], false); });
        showError('');
        setBusy(false);
      },
      setVariant: function (v, priceText) {
        if (sizeEl) sizeEl.textContent = sizeSuffix(v);
        if (priceEl) priceEl.textContent = priceText;
      }
    };
  })();

  /* ---------------------------------------------------------------------
     Engraving & gift services
     --------------------------------------------------------------------- */
  var Gift = (function () {
    var panel = drawerEl ? drawerEl.querySelector('[data-oj-panel="gift"]') : null;
    var noop = { prepare: function () {}, engravingVisible: function () { return false; }, setVariant: function () {} };
    if (!panel) return noop;

    var giftSvc = panel.querySelector('[data-oj-service="gift"]');
    var engSvc = panel.querySelector('[data-oj-service="engraving"]');
    var giftCheck = panel.querySelector('[data-oj-gift-check]');
    var giftReveal = panel.querySelector('[data-oj-gift-reveal]');
    var cardBtns = $all('[data-oj-card]', panel);
    var msgWrap = panel.querySelector('[data-oj-gift-message-wrap]');
    var msg = panel.querySelector('[data-oj-gift-message]');
    var msgCount = panel.querySelector('[data-oj-gift-count]');
    var engCheck = panel.querySelector('[data-oj-engraving-check]');
    var engReveal = panel.querySelector('[data-oj-engraving-reveal]');
    var engInput = panel.querySelector('[data-oj-engraving-input]');
    var engCount = panel.querySelector('[data-oj-engraving-count]');
    var errorEl = panel.querySelector('[data-oj-gift-error]');
    var foot = drawerEl.querySelector('[data-oj-foot="gift"]');
    var submitBtn = foot ? foot.querySelector('[data-oj-gift-submit]') : null;
    var sumSize = foot ? foot.querySelector('[data-oj-sum-size]') : null;
    var sumCollect = foot ? foot.querySelector('[data-oj-sum-collect]') : null;
    var sumPrice = foot ? foot.querySelector('[data-oj-sum-product-price]') : null;
    var sumGift = foot ? foot.querySelector('[data-oj-sum-gift]') : null;
    var sumEng = foot ? foot.querySelector('[data-oj-sum-engraving]') : null;
    var sumTotal = foot ? foot.querySelector('[data-oj-sum-total]') : null;

    var giftCfg = cfg.gift || {};
    var engCfg = cfg.engraving || {};
    var cardMode = 'include';
    var small = isSmallVariant(currentVariant);
    var busy = false;

    function engravingVisible() { return !!engSvc && !small; }
    function giftOn() { return !!(giftCheck && giftCheck.checked); }
    function engOn() { return !!(engCheck && engCheck.checked && engravingVisible()); }
    function showError(m) {
      if (!errorEl) return;
      errorEl.textContent = m || '';
      errorEl.classList.toggle('is-visible', !!m);
    }
    function update() {
      if (giftReveal) giftReveal.hidden = !giftOn();
      if (msgWrap) msgWrap.hidden = cardMode !== 'include';
      cardBtns.forEach(function (b) { b.setAttribute('aria-pressed', b.dataset.ojCard === cardMode ? 'true' : 'false'); });
      if (engSvc) engSvc.hidden = !engravingVisible();
      if (engReveal) engReveal.hidden = !engOn();
      if (sumGift) sumGift.hidden = !giftOn();
      if (sumEng) sumEng.hidden = !engOn();
      var total = variantPrice(currentVariant) + (giftOn() ? (parseInt(giftCfg.price, 10) || 0) : 0) + (engOn() ? (parseInt(engCfg.price, 10) || 0) : 0);
      if (sumTotal) sumTotal.textContent = money(total);
      if (submitBtn) {
        var available = !!(currentVariant && currentVariant.available);
        submitBtn.disabled = !available || busy;
        if (!busy) submitBtn.textContent = available ? submitBtn.dataset.label : submitBtn.dataset.labelSoldout;
      }
    }
    function sanitizeEngraving() {
      if (!engInput) return;
      var v = engInput.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8);
      if (v !== engInput.value) engInput.value = v;
      if (engCount) engCount.textContent = String(v.length);
    }
    function countMessage() {
      if (!msg) return;
      if (msg.value.length > 250) msg.value = msg.value.slice(0, 250);
      if (msgCount) msgCount.textContent = String(msg.value.length);
    }
    if (giftCheck) giftCheck.addEventListener('change', function () { showError(''); update(); if (giftCheck.checked && msg && cardMode === 'include') setTimeout(function () { msg.focus(); }, 30); });
    if (engCheck) engCheck.addEventListener('change', function () { showError(''); update(); if (engCheck.checked && engInput) setTimeout(function () { engInput.focus(); }, 30); });
    cardBtns.forEach(function (b) { b.addEventListener('click', function () { cardMode = b.dataset.ojCard; showError(''); update(); }); });
    if (msg) msg.addEventListener('input', countMessage);
    if (engInput) engInput.addEventListener('input', sanitizeEngraving);

    function openCartNotification() {
      try {
        if (window.cart_popup && typeof window.cart_popup.updateCartPopup === 'function' && window.jQuery) {
          window.cart_popup.updateCartPopup(function () {
            window.jQuery('.cart-popup-wrapper').addClass('open');
            window.jQuery('html').removeClass('cart-popup-wrapper-open').addClass('cart-popup-wrapper-open');
          });
        }
      } catch (e) { /* cart popup unavailable */ }
    }
    function submit() {
      if (busy || !currentVariant) return;
      showError('');
      var wantGift = giftOn(), wantEng = engOn();
      if (!wantGift && !wantEng) { showError(strings.errSelectService); return; }
      var props = {};
      var items = [];
      if (wantEng) {
        var text = (engInput ? engInput.value : '').trim();
        if (!text) { showError(strings.errEngraving); if (engInput) engInput.focus(); return; }
        if (!/^[A-Za-z0-9]+$/.test(text)) { showError(strings.errEngravingChars); if (engInput) engInput.focus(); return; }
        props.engraving = text;
        if (engCfg.variantId) items.push({ id: engCfg.variantId, quantity: 1 });
      }
      if (wantGift) {
        props.gift_wrap = true;
        if (cardMode === 'include') {
          var m = (msg ? msg.value : '').trim();
          if (!m) { showError(strings.errGiftMessage); if (msg) msg.focus(); return; }
          props.gift_message = m;
        }
        if (giftCfg.variantId) items.push({ id: giftCfg.variantId, quantity: 1 });
      }
      try {
        if (window.OJ_collectMarketEligible && window.OJ_collectOnly && window.OJ_collectOnly[String(currentVariant.id)]) props._collect_in_store = 'true';
      } catch (e) {}
      items.push({ id: currentVariant.id, quantity: 1, properties: props });

      busy = true;
      if (submitBtn) { submitBtn.disabled = true; submitBtn.setAttribute('aria-busy', 'true'); }
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (r) {
          if (!r.ok || (r.data && r.data.status)) throw new Error((r.data && (r.data.description || r.data.message)) || strings.errGeneric);
          Drawer.close();
          reset();
          openCartNotification();
        })
        .catch(function (err) { showError((err && err.message) || strings.errGeneric); })
        .then(function () {
          busy = false;
          if (submitBtn) submitBtn.setAttribute('aria-busy', 'false');
          update();
        });
    }
    if (submitBtn) submitBtn.addEventListener('click', submit);

    function reset() {
      if (giftCheck) giftCheck.checked = false;
      if (engCheck) engCheck.checked = false;
      if (msg) msg.value = '';
      if (engInput) engInput.value = '';
      cardMode = 'include';
      countMessage();
      sanitizeEngraving();
      showError('');
      update();
    }

    update();
    return {
      prepare: function () { showError(''); update(); },
      engravingVisible: engravingVisible,
      setVariant: function (v, priceText) {
        small = isSmallVariant(v);
        if (small && engCheck) engCheck.checked = false;
        if (sumSize) sumSize.textContent = sizeSuffix(v);
        if (sumCollect) sumCollect.textContent = isCollect(v) ? ' · ' + (strings.collectInStore || 'Collect in store') : '';
        if (sumPrice) sumPrice.textContent = priceText;
        update();
      }
    };
  })();

  /* ---------------------------------------------------------------------
     Description "read more"
     --------------------------------------------------------------------- */
  /* At-home row: drop the right fade once the last card is fully in view */
  $all('[data-oj-home-track]', root).forEach(function (track) {
    var row = track.querySelector('.oj-home__grid');
    if (!row) return;
    function update() {
      var atEnd = row.scrollWidth - row.clientWidth - Math.abs(row.scrollLeft) <= 2;
      track.classList.toggle('is-end', atEnd);
    }
    row.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', debounce(update, 150));
    update();
  });

  var readMore = root.querySelector('[data-oj-read-more]');
  if (readMore) {
    readMore.addEventListener('click', function (e) {
      e.preventDefault();
      var t = root.querySelector('[data-oj-desc-truncated]');
      var f = root.querySelector('[data-oj-desc-full]');
      if (t) t.hidden = true;
      if (f) f.hidden = false;
    });
  }

  // Initial sync (labels, sticky, drawer) for the server-selected variant.
  if (currentVariant) applyVariant(currentVariant);
  syncMarquee();
})();
